/**
 * utils/storage.js
 * localStorage persistence. All storage concerns isolated here — no UI, no React.
 *
 * Schema evolution: bump NORM_VERSION and add a forward migration block in
 * migrateData() whenever the stored shape changes during testing. loadData()
 * shallow-merges defaults (so new top-level keys always exist) then migrates.
 */
import { buildSeedData } from "./seedData";
import { suggestCategoryCode } from "./categoryCode";
import { parseFitment } from "./fitment";
import { parseMarketCodes, marketCodesToText } from "./marketCodes";
import { IJM_COMPANY, DEFAULT_CABANG_NAME } from "./company";
import { seedBranches, defaultBranchId } from "./branches";
import { generateId } from "./idGenerators";
import { ensureInternalContacts } from "./contacts";

export const STORAGE_KEY  = "bukukas2_data_v4"; // v4: SG seed replaces IJM demo items (intentional reseed)
export const NORM_VERSION = 27; // bump when a new migration is needed

/** Empty baseline shape. Seed data (buildSeedData) is used only on a truly fresh first run. */
export const defaultData = {
  items: [],
  transformations: [],
  history: [],
  recipes: [],
  managedLists: { suppliers: [], tipe: [] },
  settings: {
    lowStockThreshold: 0,
    // Editable letterhead (Phase T2-settings) — defaults to utils/company.js's IJM_COMPANY so
    // a fresh install/upgrade renders identically to before; from here on the STORED value is
    // authoritative, edited via the Pengaturan modal, never this constant.
    company: { name: IJM_COMPANY.name, addressLines: [...IJM_COMPANY.addressLines], npwp: IJM_COMPANY.npwp },
    // Invoice Cabang's own branch-name letterhead (Phase T2d) — VESTIGIAL as of the
    // multi-branch cabang feature: superseded by the real `branches` managed list below (there
    // was never going to be one single cabang name, since there was never going to be one
    // single cabang branch). Kept, unmigrated, same "harmless, not worth a migration to
    // remove" precedent `managedLists.suppliers` already established once contacts superseded
    // IT — no UI reads or writes this key anymore.
    cabangName: DEFAULT_CABANG_NAME,
    // Tempo Pembayaran redesign — the day-count TransactionFormModal defaults a NEW Belum
    // Lunas transaction's due date to, editable via Pengaturan. Replaces a hardcoded 14.
    defaultDueDays: 14,
  },
  categories: [],
  cars: [],
  siblingLinks: [],
  contacts: [],
  // Person/Role restructure (Stage 1): a person is a human being — {id, name, createdAt},
  // nothing else. `salespeople` (below) keeps its name but its records are ROLES in meaning —
  // one per person-per-selling-location, linked here via `salesperson.personId`. See
  // utils/persons.js and utils/salespeople.js's own header comments. Stage 4 moved `code` from
  // the role onto the person the same way `note` (Stage 3) already lives here — the code
  // identifies the PERSON, not the role, so it's globally unique across persons, not roles.
  persons: [],
  salespeople: [],
  branches: [],
  // Contact model redesign: the real-world firm a contact entry may optionally link to via
  // `contact.businessId` — see utils/businesses.js. Carries almost nothing itself.
  businesses: [],
  txnCounters: { pusat: {}, cabangBySales: {} },
  transactions: [],
  // Biaya Lain-Lain / Pengeluaran — our own costs (2026-10-11), see utils/expenses.js.
  expenses: [],
};

/**
 * One-time forward migrations. Early-returns when stored data is already current.
 *
 * v1: baseline schema (items / transformations / history / managedLists).
 * v2: recipes gain a `type` and move to symmetric inputs/outputs. The old assemble-only
 *     { targetSetCode, lines } becomes { type:"ASSEMBLE", inputs:lines, outputs:[{targetSetCode,1}] }.
 *     Items/history/transformations are untouched — only the recipes collection reshapes.
 * v3: recipe lines gain a cached `name` snapshot ({ internalCode, qty, name? }), backfilled
 *     from items that still exist. Lines whose item was already deleted stay nameless (accepted).
 * v4: `categories` collection derived from distinct item.category values (first-seen order).
 *     Each entry is { name, code } where code is assigned via suggestCategoryCode. Items untouched.
 * v5: item.fitment normalised from a slash-joined string to an array of car name strings.
 *     Idempotent — already-array fitment passes through unchanged.
 * v6: `cars` collection derived from distinct fitment strings across all items (first-seen
 *     order, case-insensitive dedupe). Each entry is { name }. Items untouched.
 * v7: item.marketCode normalised into a `marketCodes` array (slash-joined string → array).
 *     `marketCode` is kept as a " / "-joined mirror of `marketCodes` (legacy scaffold field,
 *     still read by some UI). Idempotent — already-array marketCodes re-derives the mirror.
 * v8: legacy `marketCode` field dropped. Backfills `marketCodes` from
 *     `item.marketCodes ?? item.marketCode` (covers items created between the 1a migration
 *     and now by inline creators that only ever wrote `marketCode: ""`), then strips the
 *     `marketCode` key entirely. Idempotent.
 * v9: `siblingLinks` (pair collection, `{a,b}[]` with `a<b`, see `utils/siblings.js`) ensured
 *     to exist as an array. The shallow-merge in loadData() already covers a totally-missing
 *     key; this migration makes it explicit and versioned, and repairs a non-array value.
 * v10: `contacts` (see `utils/contacts.js`) ensured to exist as an array. The legacy
 *     item-level `supplier` field is dropped from every item — contacts are a standalone
 *     collection now, referenced from history entries (`entry.contactId`, 4-ii), not from
 *     items. `managedLists.suppliers` (the old free-text supplier picker's option list) is
 *     left untouched — it's vestigial (no UI reads/writes it anymore) but harmless to keep.
 *     Idempotent.
 * v11: transaction ledger core (Phase T1). `salespeople`, `txnCounters`, `transactions`
 *     ensured to exist as their empty baseline shapes (`[]`, `{}`, `[]`). No existing field
 *     changes. Idempotent — already-correct-shape values pass through untouched; a
 *     wrong-shape value (e.g. `txnCounters` not a plain object) is reset to empty rather than
 *     left corrupt.
 * v12: pusat/cabang numbering split + packing + payment methods (Phase T2a). The old flat
 *     `txnCounters` map (one global `"YYYY-MM"` counter) moves under `.pusat` unchanged;
 *     `.cabangBySales` starts empty (historical numbers, pusat- or cabang-anchored alike, were
 *     minted under the old single-counter scheme and are never re-minted — invariant 8 — so
 *     there is nothing to backfill into it). Every transaction gains
 *     `packing: {collyCount:null, collyDescription:null}`. Every line's `collyNo` is coerced
 *     from int to string (`null` stays `null`). Every payment gains `method` (defaulted to
 *     `"TUNAI"` — a defensible default since every pre-T2 payment predates the concept) and
 *     `methodNote` (defaulted to `null`, not left `undefined` — `validatePayments`'s strict
 *     `methodNote !== null` check on a non-`LAINNYA` method would otherwise reject a migrated
 *     payment the instant that validation is wired in). `invoiceNumber` is never touched.
 *     Idempotent.
 * v13: `packing` restructured from `{collyCount, collyDescription}` to `{suratJalanRows:
 *     [{quantity, description}]}` (Phase T2c-1) — a surat jalan can carry several package
 *     rows, not just one. An existing non-null pair converts to a single-element array
 *     (`quantity`/`description` default to `""`, never `null`, inside a row — only the
 *     ARRAY itself is ever empty); a null/null pair converts to `[]`. Detected by shape, not
 *     just presence: `packing.suratJalanRows` already being an array means this transaction
 *     already has the v13 shape (passthrough, unchanged) — same "detect the shape, not just
 *     the key" caution as v11→v12's `txnCounters` nesting check, needed here because
 *     `loadData()`'s shallow-merge can't help (this is a per-transaction reshape, not a
 *     top-level key). `invoiceNumber` is never touched. Idempotent.
 * v14: `settings.company` (Phase T2-settings) — the printed letterhead (name/addressLines/
 *     npwp) moves from the hardcoded `utils/company.js` constant into user-editable data, so a
 *     typo or a wrong NPWP digit can be fixed from the app (a "Pengaturan" modal) instead of a
 *     code change. Ensures `settings.company` exists and is well-shaped (`name` a string,
 *     `addressLines` an array, `npwp` a string); a missing/malformed value defaults to
 *     `IJM_COMPANY`'s current values — same "nothing changes visually on upgrade" contract
 *     every other migration follows. Detected by shape, not just presence (same caution as
 *     v11→v12/v12→v13 above) — `loadData()`'s shallow-merge already fills a totally-missing
 *     `settings` key with the CURRENT `defaultData.settings` (already including `company`)
 *     before `migrateData` runs, so only an EXISTING `settings` object missing/malformed
 *     `company` actually needs fixing here. Idempotent.
 * v15: `settings.cabangName` (Phase T2d) — Invoice Cabang's own branch-name letterhead,
 *     ensured to exist as a non-empty string, defaulting to `DEFAULT_CABANG_NAME` when
 *     missing/malformed/blank. Same shape-detection caution as v14. Idempotent.
 *     **Superseded by v16 below** — this field is now vestigial.
 * v16: `branches` (multi-branch cabang feature) — the real, addable managed list of cabang
 *     branches, replacing the single flat `settings.cabangName`. A missing/malformed
 *     `data.branches` is seeded with the 3 real confirmed branches (MMC/AMIN/TMM via
 *     `utils/branches.js`'s `seedBranches()`) — NOT derived from the old `cabangName` value,
 *     since there were always 3 real branches, never just one. Every existing salesperson (no
 *     branch concept before this) is backfilled to the MMC branch specifically — the same
 *     branch `DEFAULT_CABANG_NAME` already singled out as "the" cabang before this feature —
 *     via `defaultBranchId(branches)`, so the app stays fully functional immediately after
 *     upgrade with no manual cleanup before cabang sales work again. Existing CABANG-anchored
 *     **transactions** are deliberately NOT backfilled with a `branchId` — this is still
 *     test/dev data, and fabricating a specific branch attribution for a real past transaction
 *     would be dishonest, unlike a salesperson's branch (a currently-mutable admin fact, not a
 *     historical snapshot); `utils/branches.js`'s `resolveBranchName` returns `null` for an
 *     unresolvable branchId, and every reader falls back to a generic label rather than a
 *     fabricated one. Idempotent — shape-detected (an already-populated, well-shaped
 *     `branches` array passes through untouched; a salesperson already carrying a `branchId`
 *     that resolves against the (possibly just-seeded) branches list is left alone too).
 * v17: `txnCounters.cabangBySales` reshaped from a flat all-time-per-salesperson map
 *     (`{CODE: n}`) to a map nested one level deeper by month (`{CODE: {"YYYY-MM": n}}`) — the
 *     fix for a real cabang invoice-numbering bug (`mintCabangNumber` never reset monthly,
 *     unlike `mintPusatNumber`). Existing counts are intentionally NOT carried into any month
 *     bucket — reset to `{}` is safe because (a) already-minted `invoiceNumber` strings are
 *     stored immutably on each transaction and never re-derived (invariant 8), so this can
 *     never change a number already printed, and (b) the invoice-number STRING format already
 *     embeds the month, so a reset serial can never produce a duplicate string across
 *     different months. Shape-detected (an already-nested-by-month value passes through
 *     untouched), same caution as every migration above. Idempotent.
 * v20→v21: Person/Role restructure, STAGE 1 of a five-stage plan — data layer only, no UI/
 *     write-path wiring this stage. Splits what used to be one flat "salesperson" record into
 *     two collections: `persons` (a human being — {id, name, createdAt}, nothing else) and the
 *     existing `salespeople` collection, whose records become ROLES in MEANING (not in name —
 *     see salespeople.js's own header comment for why the collection keeps its old name).
 *
 *     Each existing salesperson KEEPS ITS OWN id — every `txn.salespersonId` already points at
 *     it and none needs rewriting — and gains `personId` (a brand-new person minted 1:1, name
 *     copied verbatim) and `commissionRate: 0` (stored, unused until a later stage).
 *     `branchId`/`code`/`name` are otherwise untouched. Two existing salespeople are NEVER
 *     matched/merged into one person by name, even if they happen to share one — this app has
 *     no way to know two existing records are "the same real person" from data alone, and doing
 *     so would be a fabrication, not a migration.
 *
 *     `name` is deliberately KEPT on every migrated role, even though a role has no name of its
 *     own in the target model — dozens of existing UI call sites (KelolaSalesModal,
 *     SalespersonPicker, TxnRowActions, TransactionDetailModal, App.js's own spName/salesHit
 *     lookups) still read `sp.name` directly and are explicitly out of this stage's touch
 *     scope; removing it would break live display today, not just a future migration. It is now
 *     VESTIGIAL — the same "leave it, mark it, don't delete it" precedent `settings.cabangName`/
 *     `managedLists.suppliers` already established in this codebase — a frozen snapshot of the
 *     person's name AT MIGRATION TIME that can drift out of sync the moment something renames
 *     the person via `updatePerson` (nothing does yet — no write path calls it this stage). A
 *     later stage that wires the UI onto `resolveSalespersonName`/`personId` can then safely
 *     stop reading/writing this field.
 *
 *     Shape-detected, not just presence-detected (same caution as every migration above): a
 *     salesperson that ALREADY carries a `personId` resolving against `data.persons` passes
 *     through untouched (only `commissionRate` is normalized if missing/malformed); only a
 *     salesperson missing/dangling one gets a fresh person minted for it. A malformed
 *     `d.salespeople`/`d.persons` (not an array) is treated as empty rather than crashing.
 *     Idempotent.
 * v21→v22: Person/Role restructure, STAGE 2 — every read site that used to read a role's own
 *     `sp.name` now resolves through the linked person instead (`resolveSalespersonName`/
 *     `displaySalesperson`, salespeople.js). With no reader left, the role's vestigial `name`
 *     field (kept by v20→v21 above only because those readers still existed) is stripped.
 *
 *     Shape-detected, not presence-detected: a salesperson that no longer carries a `name` key
 *     at all has already been migrated (or was created fresh, post-Stage-2) and passes through
 *     untouched — this is what makes re-running the chain safe.
 *
 *     A salesperson that STILL carries `name` is only stripped after confirming its
 *     `personId` resolves to a real person in `data.persons` AND that person's own `name`
 *     matches the role's frozen one exactly. Both must hold — this is the one remaining copy of
 *     the name if either check fails, so stripping on a mismatch would be real, irreversible
 *     data loss, not a cleanup. Per v20→v21's own migration (every role minted its person 1:1,
 *     name copied verbatim) and the fact that nothing in this app calls `updatePerson` before
 *     this stage, the two should always agree — this migration verifies that rather than
 *     assuming it, because a hand-edited or externally-modified data blob could in principle
 *     disagree. A role that fails the check is left completely untouched (its `name` survives)
 *     and a `console.warn` names the role's id, so a real mismatch is visible rather than
 *     silently swallowed — see `migrateData`'s own test coverage for this exact case.
 *
 *     Malformed `d.persons`/`d.salespeople` (not arrays) are treated as empty rather than
 *     crashing, same as every migration above. Idempotent.
 * v23→v24: Person/Role restructure, STAGE 4 — `code` moves from the role to the person. The
 *     code identifies the PERSON (Budi is BU wherever he works), not the role, so every one of
 *     a person's roles must share one code from here on, and that code must be globally unique
 *     across every PERSON, not every role.
 *
 *     For each person: gather every role (`data.salespeople`) whose `personId` points at them,
 *     sorted by `createdAt` ascending, and adopt the EARLIEST one's `code` as `person.code` —
 *     this is the state the old per-role-unique-code rule could produce (one person ending up
 *     with a different code per role, e.g. AL at TMM and AL2 at AMIN); the earliest code is the
 *     one that's been on record longest, so it's the one kept. Every role's own `code` field is
 *     then stripped (a role has none of its own from this version on).
 *
 *     **Safety check before discarding a non-earliest role's code**: a role whose code is being
 *     discarded is checked against `data.transactions` (`t.salespersonId === role.id`) first —
 *     if it has ANY real transaction, its `code` field is left in place (vestigial, never read
 *     by anything from this version on — same "leave it, mark it, don't delete it" precedent
 *     `settings.cabangName`/the v21→v22 name-mismatch case already established) and a
 *     `console.warn` names the role/person, rather than silently discarding a code that might
 *     still matter to someone reading raw storage. This never blocks the rest of the migration —
 *     the person's own canonical `code` is still set from the earliest role regardless, and
 *     already-minted `invoiceNumber` strings are untouched either way (invariant 8: immutable
 *     once minted, never re-derived from a role's code after the fact).
 *
 *     Shape-detected, not presence-detected: a person whose `code` is already a valid 1-4-char
 *     `[A-Z0-9]` string passes through untouched (this run's own re-entry guard, and the signal
 *     a role's own `code` key has already been stripped — checked independently per role via
 *     `"code" in s`, not derived from the person's own state, so a role legitimately left in
 *     place by the safety check above is never re-processed as if it were unmigrated). A person
 *     with no role ever carrying a code (shouldn't happen for real Stage-1-onward data, but
 *     defensive) is left without a `code` rather than fabricating one. A role whose `personId`
 *     doesn't resolve to any real person at all (a dangling reference — external data
 *     corruption, cannot occur through this app's own write paths) is likewise left with its
 *     `code` untouched rather than guessed at — same "can't confirm, don't touch" caution the
 *     v21→v22 name-mismatch case above already established. Malformed
 *     `d.persons`/`d.salespeople`/`d.transactions` (not arrays) are treated as empty rather than
 *     crashing, same as every migration above. Idempotent.
 */
function migrateData(data) {
  if ((data._normVersion || 0) >= NORM_VERSION) return data;

  let d = data;

  // v1 -> v2: typed, symmetric recipes. Idempotent — already-new-shape recipes pass through.
  if ((d._normVersion || 0) < 2) {
    d = {
      ...d,
      recipes: (d.recipes || []).map((r) => {
        if (r.type && Array.isArray(r.inputs) && Array.isArray(r.outputs)) return r;
        return {
          id: r.id,
          name: r.name,
          note: r.note || "",
          type: "ASSEMBLE",
          inputs: Array.isArray(r.lines)
            ? r.lines.map((l) => ({ internalCode: l.internalCode, qty: l.qty }))
            : [],
          outputs: r.targetSetCode
            ? [{ internalCode: r.targetSetCode, qty: 1 }]
            : [],
        };
      }),
    };
  }

  // v2 -> v3: recipe lines gain a cached `name` snapshot, backfilled from items that
  // still exist. Lines whose item was already deleted stay nameless (accepted).
  if ((d._normVersion || 0) < 3) {
    const nameByCode = new Map((d.items || []).map((it) => [it.internalCode, it.name]));
    const withName = (l) => {
      if (l.name != null) return l;
      const nm = nameByCode.get(l.internalCode);
      return nm != null ? { ...l, name: nm } : l;
    };
    d = {
      ...d,
      recipes: (d.recipes || []).map((r) => ({
        ...r,
        inputs: (r.inputs || []).map(withName),
        outputs: (r.outputs || []).map(withName),
      })),
    };
  }

  // v4 -> v5: normalise item.fitment from slash-joined string to array. Idempotent.
  if ((d._normVersion || 0) < 5) {
    d = {
      ...d,
      items: (d.items || []).map((item) => ({ ...item, fitment: parseFitment(item.fitment) })),
    };
  }

  // v3 -> v4: derive managed categories from existing items. Idempotent — pre-existing
  // categories are preserved; only new distinct category names are appended.
  if ((d._normVersion || 0) < 4) {
    const cats  = [...(d.categories || [])];
    const codes = cats.map((c) => c.code);
    const seen  = new Set(cats.map((c) => (c.name || "").trim().toUpperCase()));
    for (const it of (d.items || [])) {
      const nm = (it.category || "").trim();
      if (!nm) continue;
      const key = nm.toUpperCase();
      if (seen.has(key)) continue;
      const code = suggestCategoryCode(nm, codes);
      cats.push({ name: nm, code });
      codes.push(code);
      seen.add(key);
    }
    d = { ...d, categories: cats };
  }

  // v5 -> v6: derive managed cars list from item fitment arrays. Idempotent — pre-existing
  // cars are preserved; only new distinct names (case-insensitive) are appended.
  if ((d._normVersion || 0) < 6) {
    const cars = [...(d.cars || [])];
    const seen = new Set(cars.map((c) => (c.name || "").trim().toUpperCase()));
    for (const it of (d.items || [])) {
      for (const car of parseFitment(it.fitment)) {
        const nm = car.trim();
        if (!nm) continue;
        const key = nm.toUpperCase();
        if (seen.has(key)) continue;
        cars.push({ name: nm });
        seen.add(key);
      }
    }
    d = { ...d, cars };
  }

  // v6 -> v7: normalise item.marketCode into a marketCodes array, keeping marketCode as a
  // " / "-joined mirror. Idempotent — already-array marketCodes re-derives the mirror.
  if ((d._normVersion || 0) < 7) {
    d = {
      ...d,
      items: (d.items || []).map((item) => {
        const marketCodes = parseMarketCodes(item.marketCodes ?? item.marketCode);
        return { ...item, marketCodes, marketCode: marketCodesToText(marketCodes) };
      }),
    };
  }

  // v7 -> v8: drop the legacy marketCode field. Backfills marketCodes from
  // marketCodes ?? marketCode first (covers inline-created items that only ever got
  // marketCode: ""), then strips marketCode entirely. Idempotent.
  if ((d._normVersion || 0) < 8) {
    d = {
      ...d,
      items: (d.items || []).map((item) => {
        const marketCodes = parseMarketCodes(item.marketCodes ?? item.marketCode);
        const { marketCode, ...rest } = item;
        return { ...rest, marketCodes };
      }),
    };
  }

  // v8 -> v9: ensure siblingLinks exists as an array. Idempotent.
  if ((d._normVersion || 0) < 9) {
    d = { ...d, siblingLinks: Array.isArray(d.siblingLinks) ? d.siblingLinks : [] };
  }

  // v9 -> v10: ensure contacts exists as an array, and drop the legacy item.supplier field
  // (contacts are now a standalone collection, referenced from history, not from items).
  // Idempotent.
  if ((d._normVersion || 0) < 10) {
    d = {
      ...d,
      contacts: Array.isArray(d.contacts) ? d.contacts : [],
      items: (d.items || []).map((item) => {
        const { supplier, ...rest } = item;
        return rest;
      }),
    };
  }

  // v10 -> v11: transaction ledger core — salespeople, txnCounters, transactions ensured to
  // exist as their empty baseline shapes. No existing field changes. Idempotent.
  if ((d._normVersion || 0) < 11) {
    d = {
      ...d,
      salespeople: Array.isArray(d.salespeople) ? d.salespeople : [],
      txnCounters: d.txnCounters && typeof d.txnCounters === "object" && !Array.isArray(d.txnCounters)
        ? d.txnCounters
        : {},
      transactions: Array.isArray(d.transactions) ? d.transactions : [],
    };
  }

  // v11 -> v12: pusat/cabang numbering split + packing + payment methods (Phase T2a). The
  // old flat counter map moves under .pusat as-is; .cabangBySales starts empty. Idempotent.
  //
  // Shape detection is needed (not just "is a plain object"): loadData()'s shallow-merge
  // already fills a totally-missing txnCounters key with the CURRENT defaultData shape
  // (already { pusat, cabangBySales }) before migrateData ever runs — a pre-v11 fixture
  // with no txnCounters key at all would otherwise arrive here looking like an object and
  // get wrongly re-nested a second time (`{ pusat: { pusat:{}, cabangBySales:{} }, ... }`).
  // "Already nested" is detected by the presence of either sub-key; only a genuine old
  // flat map (`{ "YYYY-MM": n }`, no pusat/cabangBySales keys) gets moved under .pusat.
  if ((d._normVersion || 0) < 12) {
    const rawCounters = d.txnCounters;
    const isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
    const alreadyNested = isPlainObject(rawCounters)
      && ("pusat" in rawCounters || "cabangBySales" in rawCounters);
    const pusat = alreadyNested
      ? (isPlainObject(rawCounters.pusat) ? rawCounters.pusat : {})
      : (isPlainObject(rawCounters) ? rawCounters : {});
    const cabangBySales = alreadyNested && isPlainObject(rawCounters.cabangBySales)
      ? rawCounters.cabangBySales
      : {};
    d = {
      ...d,
      txnCounters: { pusat, cabangBySales },
      transactions: (d.transactions || []).map((txn) => ({
        ...txn,
        packing: { collyCount: null, collyDescription: null },
        lines: (txn.lines || []).map((l) => ({
          ...l,
          collyNo: l.collyNo == null ? null : String(l.collyNo),
        })),
        payments: (txn.payments || []).map((p) => ({
          ...p,
          method: p.method || "TUNAI",
          methodNote: p.methodNote ?? null,
        })),
      })),
    };
  }

  // v12 -> v13: packing restructured to { suratJalanRows: [{quantity, description}] }
  // (Phase T2c-1). Idempotent — a transaction whose packing.suratJalanRows is already an
  // array passes through untouched; only the old { collyCount, collyDescription } shape
  // (or a missing/malformed packing) is converted.
  if ((d._normVersion || 0) < 13) {
    d = {
      ...d,
      transactions: (d.transactions || []).map((txn) => {
        const p = txn.packing;
        if (p && Array.isArray(p.suratJalanRows)) return txn; // already v13-shaped
        const collyCount = p ? p.collyCount : null;
        const collyDescription = p ? p.collyDescription : null;
        const suratJalanRows = (collyCount != null || collyDescription != null)
          ? [{ quantity: collyCount || "", description: collyDescription || "" }]
          : [];
        return { ...txn, packing: { suratJalanRows } };
      }),
    };
  }

  // v13 -> v14: settings.company (Phase T2-settings) — the shared letterhead becomes
  // user-editable data instead of a hardcoded constant. Idempotent — an already well-shaped
  // company object passes through untouched; anything missing/malformed defaults to
  // IJM_COMPANY's current values.
  if ((d._normVersion || 0) < 14) {
    const isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
    const existing = d.settings && d.settings.company;
    const wellShaped = isPlainObject(existing)
      && typeof existing.name === "string"
      && Array.isArray(existing.addressLines)
      && typeof existing.npwp === "string";
    const company = wellShaped
      ? existing
      : { name: IJM_COMPANY.name, addressLines: [...IJM_COMPANY.addressLines], npwp: IJM_COMPANY.npwp };
    d = { ...d, settings: { ...(d.settings || {}), company } };
  }

  // v14 -> v15: settings.cabangName (Phase T2d) — Invoice Cabang's own branch-name letterhead,
  // a separate entity from settings.company, becomes user-editable data the same way company
  // did in v13->v14. Idempotent — an already-well-shaped non-empty string passes through
  // untouched; anything missing/malformed defaults to DEFAULT_CABANG_NAME.
  if ((d._normVersion || 0) < 15) {
    const existing = d.settings && d.settings.cabangName;
    const wellShaped = typeof existing === "string" && existing.trim() !== "";
    const cabangName = wellShaped ? existing : DEFAULT_CABANG_NAME;
    d = { ...d, settings: { ...(d.settings || {}), cabangName } };
  }

  // v15 -> v16: branches collection (multi-branch cabang feature) + salespeople.branchId
  // backfill. data.branches seeded with the 3 real branches (MMC/AMIN/TMM), not just one name
  // derived from the old settings.cabangName default -- confirmed there are 3 real branches.
  // Every existing salesperson with no RESOLVABLE branchId (missing entirely -- no branch
  // concept before this -- or pointing at a branch that doesn't exist) is backfilled to the MMC
  // branch specifically -- the same branch settings.cabangName/DEFAULT_CABANG_NAME already
  // singled out as "the" cabang before this feature -- so the app stays fully functional
  // immediately after upgrade with no manual cleanup before cabang sales work again. A
  // genuinely-present `branchId: null` is left alone, never backfilled -- that's already a
  // deliberate, valid value (Pusat) everywhere else in this app, not a hole to fill; real
  // pre-v16 data can never actually have it (the field didn't exist yet), so this only matters
  // for data that reaches this migration block some other way. Existing CABANG-anchored
  // transactions are deliberately NOT backfilled with a branchId (see the Transaction entry in
  // the root doc) -- this is still test/dev data, and fabricating a specific branch attribution
  // for a real past transaction would be dishonest; resolveBranchName's own null-return
  // contract (utils/branches.js) is what every reader of an old transaction's branchId falls
  // back through. Idempotent -- shape-detected, not just presence-detected.
  if ((d._normVersion || 0) < 16) {
    const isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
    const existingBranches = d.branches;
    const branchesWellShaped = Array.isArray(existingBranches) && existingBranches.length > 0 &&
      existingBranches.every((b) => isPlainObject(b) && typeof b.id === "string" && typeof b.name === "string" && b.name.trim() !== "");
    const branches = branchesWellShaped ? existingBranches : seedBranches();

    const defaultId = defaultBranchId(branches);
    const branchIds = new Set(branches.map((b) => b.id));
    // `branchId: null` is a deliberate, valid value meaning Pusat everywhere else in this app —
    // real pre-v16 data never has `branchId` present at all (the field didn't exist yet), so
    // this only ever matters for an already-null value that reaches this block some other way;
    // it must be preserved, not treated the same as "missing" and silently backfilled to MMC.
    const salespeople = (d.salespeople || []).map((s) => {
      const hasBranchId = Object.prototype.hasOwnProperty.call(s, "branchId");
      const resolvable = hasBranchId && (s.branchId === null || branchIds.has(s.branchId));
      return resolvable ? s : { ...s, branchId: defaultId };
    });

    d = { ...d, branches, salespeople };
  }

  // v16 -> v17: txnCounters.cabangBySales reshaped from a flat all-time-per-salesperson map
  // ({CODE: n}) to a map nested one level deeper by month ({CODE: {"YYYY-MM": n}}) -- the fix
  // for the cabang invoice-numbering bug (see utils/invoiceNumbers.js). Existing counts are
  // intentionally NOT carried into any month bucket -- reset to {} is safe because (a)
  // already-minted invoiceNumber strings are stored immutably on each transaction and never
  // re-derived (invariant 8), so this can never change a number already printed, and (b) the
  // invoice-number STRING format already embeds the month, so a reset serial can never produce
  // a duplicate string across different months. Shape-detected, not just presence-detected,
  // same caution every migration above follows.
  if ((d._normVersion || 0) < 17) {
    const isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
    const rawCounters = d.txnCounters;
    const pusat = isPlainObject(rawCounters) && isPlainObject(rawCounters.pusat) ? rawCounters.pusat : {};
    const rawCabangBySales = isPlainObject(rawCounters) ? rawCounters.cabangBySales : undefined;
    const alreadyNestedByMonth = isPlainObject(rawCabangBySales) &&
      Object.values(rawCabangBySales).every((v) => isPlainObject(v));
    const cabangBySales = alreadyNestedByMonth ? rawCabangBySales : {};

    d = { ...d, txnCounters: { pusat, cabangBySales } };
  }

  // v17 -> v18: settings.defaultDueDays (Tempo Pembayaran redesign) -- the hardcoded 14-day
  // Belum Lunas default becomes user-editable data, same "move a constant into settings" shape
  // as v13->v14's settings.company. Idempotent -- shape-detected: an existing well-formed
  // positive number passes through untouched, anything missing/malformed defaults to 14.
  if ((d._normVersion || 0) < 18) {
    const existing = d.settings && d.settings.defaultDueDays;
    const wellShaped = typeof existing === "number" && Number.isFinite(existing) && existing > 0;
    const defaultDueDays = wellShaped ? existing : 14;
    d = { ...d, settings: { ...(d.settings || {}), defaultDueDays } };
  }

  // v18 -> v19: transaction.costItems (cost items Brief 1) -- a new itemized array of
  // biaya/tagihan rows per transaction, alongside `payments`. Idempotent -- shape-detected: a
  // transaction whose costItems is already an array passes through untouched; missing/
  // malformed defaults to []. This brief only stores the shape (no Total/stock/history effect
  // yet), so an empty default is always safe and changes nothing visible on upgrade.
  if ((d._normVersion || 0) < 19) {
    d = {
      ...d,
      transactions: (d.transactions || []).map((txn) =>
        Array.isArray(txn.costItems) ? txn : { ...txn, costItems: [] }
      ),
    };
  }

  // v19 -> v20: payment.paidAt -- a new user-settable real-world payment date/time, separate
  // from `at` (which stays a pure server-stamped audit/insertion timestamp, unchanged, never
  // shown/editable). Idempotent -- shape-detected: a payment row whose paidAt is already a
  // non-empty string passes through untouched; missing/malformed defaults to that row's OWN
  // existing `at` (not "now", not a fabricated date) -- `at` was the payment's only timestamp
  // before this version, so it's the truthful, non-destructive default for old data.
  if ((d._normVersion || 0) < 20) {
    d = {
      ...d,
      transactions: (d.transactions || []).map((txn) => ({
        ...txn,
        payments: (txn.payments || []).map((p) =>
          typeof p.paidAt === "string" && p.paidAt ? p : { ...p, paidAt: p.at || null }
        ),
      })),
    };
  }

  // v20 -> v21: Person/Role restructure, Stage 1 (data layer only). Every existing salesperson
  // gains a personId (a brand-new person minted 1:1, name copied verbatim -- never matched/
  // merged by name across records) and a commissionRate defaulted to 0. Shape-detected: a
  // salesperson already carrying a personId that resolves against data.persons is left alone
  // (only commissionRate gets normalized if missing/malformed). See this function's own header
  // comment above for the full "why name is kept" reasoning. Idempotent.
  if ((d._normVersion || 0) < 21) {
    const existingPersons = Array.isArray(d.persons) ? d.persons : [];
    const personIds = new Set(existingPersons.map((p) => p.id));
    const mintedPersons = [];
    const salespeopleArr = Array.isArray(d.salespeople) ? d.salespeople : [];

    const salespeople = salespeopleArr.map((s) => {
      const commissionRate = typeof s.commissionRate === "number" && Number.isFinite(s.commissionRate) && s.commissionRate >= 0
        ? s.commissionRate
        : 0;
      const alreadyLinked = typeof s.personId === "string" && personIds.has(s.personId);
      if (alreadyLinked) {
        return commissionRate === s.commissionRate ? s : { ...s, commissionRate };
      }
      const person = { id: generateId(), name: s.name, createdAt: s.createdAt || new Date().toISOString() };
      mintedPersons.push(person);
      personIds.add(person.id);
      return { ...s, personId: person.id, commissionRate };
    });

    d = { ...d, persons: [...existingPersons, ...mintedPersons], salespeople };
  }

  // v21 -> v22: Person/Role restructure, Stage 2. Every UI read site now resolves a role's name
  // through its linked person (resolveSalespersonName/displaySalesperson) instead of the role's
  // own frozen `name` -- so that field is stripped here. Shape-detected: a role with no `name`
  // key at all has already been migrated and passes through untouched (idempotent). A role that
  // still carries `name` is stripped ONLY when its personId resolves to a real person whose OWN
  // name matches the role's frozen one exactly -- if either check fails, this is the one
  // remaining copy of that name, so it's left in place and reported via console.warn rather
  // than destroyed. See this function's own header comment above for the full reasoning.
  if ((d._normVersion || 0) < 22) {
    const persons = Array.isArray(d.persons) ? d.persons : [];
    const salespeopleArr = Array.isArray(d.salespeople) ? d.salespeople : [];

    const salespeople = salespeopleArr.map((s) => {
      if (!Object.prototype.hasOwnProperty.call(s, "name")) return s; // already stripped

      const person = persons.find((p) => p.id === s.personId);
      if (!person || person.name !== s.name) {
        console.warn(
          `storage: v21->v22 migration left salesperson ${s.id}'s vestigial name in place -- ` +
          `its personId does not resolve to a person carrying the same name`
        );
        return s;
      }

      const { name, ...stripped } = s;
      return stripped;
    });

    d = { ...d, salespeople };
  }

  // v22 -> v23: Person/Role restructure, Stage 3 batch 1 -- two small, independent additive
  // fields bundled into one migration since neither depends on the other: person.note (a
  // free-text disambiguator, e.g. "Abun tua" vs "Abun muda" -- displayed starting a later
  // batch, but storable/settable from this one) and salesperson.active (defaults true --
  // the flag a later batch's "remove from branch" will flip to false instead of ever deleting
  // the role, so its id and past transactions survive). Idempotent -- shape-detected, not just
  // presence-detected: a person whose note is already a string passes through untouched
  // (missing/malformed defaults to "", same as every other free-text default in this file); a
  // role whose active is already a real boolean passes through untouched (missing/malformed
  // defaults to true -- every role that predates this feature has, by definition, never been
  // deactivated, so true is the only honest default).
  if ((d._normVersion || 0) < 23) {
    d = {
      ...d,
      persons: (d.persons || []).map((p) =>
        typeof p.note === "string" ? p : { ...p, note: "" }
      ),
      salespeople: (d.salespeople || []).map((s) =>
        typeof s.active === "boolean" ? s : { ...s, active: true }
      ),
    };
  }

  // v23 -> v24: Person/Role restructure, Stage 4 -- `code` moves from the role to the person.
  // See this function's own header comment above for the full reasoning (why the earliest role
  // by createdAt donates its code, and why a non-earliest role's code is left in place rather
  // than discarded whenever it has real transactions of its own).
  if ((d._normVersion || 0) < 24) {
    const CODE_RE = /^[A-Z0-9]{1,4}$/;
    const persons = Array.isArray(d.persons) ? d.persons : [];
    const salespeople = Array.isArray(d.salespeople) ? d.salespeople : [];
    const transactions = Array.isArray(d.transactions) ? d.transactions : [];

    const nextPersons = persons.map((p) => {
      if (typeof p.code === "string" && CODE_RE.test(p.code)) return p; // already migrated

      const ownRoles = salespeople
        .filter((s) => s.personId === p.id && typeof s.code === "string" && s.code)
        .slice()
        .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      if (ownRoles.length === 0) return p; // no role ever carried a code -- nothing to adopt

      return { ...p, code: ownRoles[0].code };
    });

    const codeByPersonId = new Map(nextPersons.map((p) => [p.id, p.code]));

    const nextSalespeople = salespeople.map((s) => {
      if (!Object.prototype.hasOwnProperty.call(s, "code")) return s; // already stripped
      if (!codeByPersonId.has(s.personId)) return s; // dangling personId -- same "can't confirm, don't touch" caution as v21->v22's own name check

      const canonicalCode = codeByPersonId.get(s.personId);
      if (s.code === canonicalCode) {
        const { code, ...stripped } = s;
        return stripped;
      }

      // This role's code is being discarded (a different, non-earliest role for this person).
      // Safety check: never silently discard a code that still has real transaction history.
      const hasTransactions = transactions.some((t) => t.salespersonId === s.id);
      if (hasTransactions) {
        console.warn(
          `storage: v23->v24 migration left salesperson ${s.id}'s vestigial code "${s.code}" ` +
          `in place -- it has real transactions and the person's canonical code is now ` +
          `"${canonicalCode}"`
        );
        return s;
      }

      const { code, ...stripped } = s;
      return stripped;
    });

    d = { ...d, persons: nextPersons, salespeople: nextSalespeople };
  }

  // v24 -> v25: Contact model redesign. The contacts collection gets a new shape (list-scoped
  // PUSAT/CABANG split, addresses[], businessId, personIds[], labels[], npwp/pkp/contactPerson/
  // ekspedisi, internal/internalBranchId) incompatible with the old flat
  // {id,name,address,city,phone,email,notes} shape. All stored contacts are dummy data (this
  // feature's own build brief) — wiped outright rather than migrated field-by-field.
  // `history`/`transactions` entries keep whatever `contactId` they already had: every read path
  // already treats an unresolved contactId as "not found" and renders a plain fallback (see root
  // CLAUDE.md's Contact entry), so nothing else needs clearing. `ensureInternalContacts` then
  // rebuilds the app-maintained internal contacts from the CURRENT branches collection (already
  // migrated by this point — blocks run in version order) — one CABANG-list "Pusat" stand-in,
  // plus one PUSAT-list stand-in per real branch.
  if ((d._normVersion || 0) < 25) {
    d = ensureInternalContacts({ ...d, contacts: [] });
  }

  // v25 -> v26: Biaya Lain-Lain (the cost system the client confirmed, 2026-10-11). Our own
  // costs used to be "biaya" rows inside a transaction's `costItems`; they now live in their own
  // list, `data.expenses` (utils/expenses.js), linked to the transaction. Every "biaya" row is
  // MOVED there, losing nothing: same amount, same transaction, booked on the transaction's own
  // date and location; the old description becomes the note, and the category is the honest
  // "Lainnya" (nobody ever chose one). The id is derived from the row's own ids, so two devices
  // upgrading the same shared books at once produce the identical result. The transaction's own
  // `editLog` is left alone (history is never rewritten) — an old entry still reads "Biaya".
  // Idempotent: a transaction with no "biaya" row is untouched.
  if ((d._normVersion || 0) < 26) {
    const moved = [];
    const transactions = (d.transactions || []).map((t) => {
      const biaya = (t.costItems || []).filter((c) => c.type === "biaya");
      if (biaya.length === 0) return t;
      for (const c of biaya) {
        moved.push({
          id: `exp-${t.id}-${c.id}`,
          date: t.date,
          location: t.location === "CABANG" ? "CABANG" : "PUSAT",
          branchId: t.location === "CABANG" ? t.branchId || null : null,
          category: "Lainnya",
          paidTo: "",
          amount: Number(c.amount) || 0,
          note: c.description || "",
          txnId: t.id,
          createdAt: t.createdAt || null,
        });
      }
      return { ...t, costItems: (t.costItems || []).filter((c) => c.type !== "biaya") };
    });
    d = { ...d, transactions, expenses: [...(Array.isArray(d.expenses) ? d.expenses : []), ...moved] };
  }

  // v26 -> v27 (2026-10-11): deleting a transaction now deletes its Biaya Lain-Lain too. A cost
  // left behind by a transaction deleted BEFORE that change (linked to a txnId that no longer
  // exists) is removed here, on Chalton's request. Standalone costs (txnId null) are untouched;
  // nothing changes when there is no such orphan (same reference).
  if ((d._normVersion || 0) < 27 && Array.isArray(d.expenses)) {
    const txnIds = new Set((d.transactions || []).map((t) => t.id));
    const kept = d.expenses.filter((e) => !e.txnId || txnIds.has(e.txnId));
    if (kept.length !== d.expenses.length) d = { ...d, expenses: kept };
  }

  return { ...d, _normVersion: NORM_VERSION };
}

/**
 * Bring a stored data object (from localStorage OR the shared online copy) up to the current
 * shape: shallow-merge `defaultData` (so a brand-new top-level key always exists), then run
 * every pending migration. Pure. Both load paths go through this one function so they can
 * never upgrade data differently. Returns `{ merged, migrated }` — `migrated !== merged`
 * means a migration actually changed something (the caller decides whether to re-save).
 */
export function prepareLoadedData(parsed) {
  const merged = { ...defaultData, ...parsed };
  return { merged, migrated: migrateData(merged) };
}

/**
 * The starting books for a brand-new SHARED (online) copy — multi-device sync, decided
 * 2026-10-07: the 3 real branches and the 8 seed cars only, with NO sample items (unlike
 * `loadData`'s local first run, which still seeds the 7 SIDE GEAR test items for development).
 * Also creates the app-maintained internal transfer contacts up front. Pure.
 */
export function buildStarterBooks() {
  const seed = buildSeedData();
  return ensureInternalContacts({
    ...defaultData,
    cars: seed.cars,
    branches: seed.branches,
    _normVersion: NORM_VERSION,
  });
}

/**
 * Load persisted data. On first run (no key) seeds the sample IJM catalog and
 * persists it. Otherwise parses, shallow-merges defaults, migrates, and re-persists
 * if the migration changed anything. Any error falls back to fresh seed data.
 */
export function loadData() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const seeded = { ...defaultData, ...buildSeedData(), _normVersion: NORM_VERSION };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded)); } catch { /* ignore quota */ }
      return seeded;
    }
    const { merged, migrated } = prepareLoadedData(JSON.parse(raw));
    if (migrated !== merged) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated)); } catch { /* ignore */ }
    }
    return migrated;
  } catch {
    return { ...defaultData, ...buildSeedData(), _normVersion: NORM_VERSION };
  }
}

/** Persist the full app data object. Returns true on success, false on quota/write error. */
export function saveData(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (e) {
    console.error("[BukuKas2] saveData failed:", e);
    return false;
  }
}
